<template>
  <loading v-if="loading" />
  <message v-else-if="submitted" type="success">
    <p>Thank you, your {{ type }} has been submitted</p>
  </message>
  <message v-else-if="alreadyComplete" type="error">
    <p>Sorry, you can only complete each form once.</p>
  </message>
  <validation-observer
    ref="observer"
    v-slot="{ invalid }"
    tag="form"
    @submit.prevent="onSubmitForm"
    class="mft-form-build"
    v-else
  >
    <message v-if="submitted && errors.items.length > 0" type="danger">
      <p>
        There was problems submitting your form, please look at the highlighted
        fields below.
      </p>
    </message>
    <section class="row" v-for="(element, index) in elements" :key="index">
      <div v-if="element.type == 'base-row'" class="col-xs-12">
        <div class="row">
          <div :class="`col-xs-${element.properties.leftColumnWidth}`">
            <form-builder-column
              :element="element.properties.leftColumn"
              v-if="
                ['base-heading', 'text-block'].includes(
                  JSON.parse(element.properties.leftColumn).type
                )
              "
            />
            <form-builder-column
              :element="element.properties.leftColumn"
              :id="element.id"
              v-else
              v-model="
                formResponse[
                  getLabel(element.properties.leftColumn, element.id, '-1')
                ]
              "
            />
          </div>
          <div :class="`col-xs-${element.properties.rightColumnWidth}`">
            <form-builder-column
              :element="element.properties.rightColumn"
              v-if="
                ['base-heading', 'text-block'].includes(
                  JSON.parse(element.properties.rightColumn).type
                )
              "
            />
            <form-builder-column
              :element="element.properties.rightColumn"
              v-else
              v-model="
                formResponse[
                  getLabel(element.properties.rightColumn, element.id, '-2')
                ]
              "
            />
          </div>
        </div>
      </div>
      <div v-else-if="element.type == 'base-heading'" class="col-xs-12">
        <base-heading
          :type="element.properties.type"
          :text="element.properties.text"
        />
      </div>
      <div v-else-if="element.type == 'text-block'" class="col-xs-12">
        <text-block v-html="element.content" />
      </div>
      <div
        v-else-if="element.type == 'form-textfield'"
        class="col-xs-12 bordered"
      >
        <validation-provider
          :rules="{
            required: element.properties.required,
            min: element.properties.minlength || 0,
            max: element.properties.maxlength || false,
          }"
        >
          <template slot-scope="{ errors }">
            <form-textfield
              :tabindex="index"
              v-bind="element.properties"
              :name="element.properties.label"
              v-model="
                formResponse[`${element.properties.label}_${element.id}`]
              "
            />
            <p class="error" v-if="errors.length > 0">
              {{ errors[0].replace("{field} ", "") }}
            </p>
          </template>
        </validation-provider>
      </div>
      <div v-else-if="element.type == 'form-select'" class="col-xs-12 bordered">
        <validation-provider
          :rules="{ required: { allowFalse: !element.properties.required } }"
        >
          <template slot-scope="{ errors }">
            <form-select
              :tabindex="index + 1"
              :multiple="element.properties.multiple"
              v-bind="element.properties"
              @update="updateField($event, element.properties.label)"
              :value="formResponse[element.properties.label]"
              :name="element.properties.label"
              @keyup="onKeyUp($event, element.properties.label)"
              v-model="
                formResponse[`${element.properties.label}_${element.id}`]
              "
            />
            <p class="error" v-if="errors.length > 0">
              {{ errors[0].replace("{field} ", "") }}
            </p>
          </template>
        </validation-provider>
      </div>
      <div
        v-else-if="element.type == 'form-dateinput'"
        class="col-xs-12 bordered"
      >
        <validation-provider
          :rules="{ required: { allowFalse: !element.properties.required } }"
        >
          <template slot-scope="{ errors }">
            <form-dateinput
              :tabindex="index"
              v-bind="element.properties"
              @update="updateField($event, element.properties.label)"
              :value="formResponse[element.properties.label]"
              :name="element.properties.label"
              @keyup="onKeyUp($event, element.properties.label)"
              v-model="
                formResponse[`${element.properties.label}_${element.id}`]
              "
              :mindate="element.properties.mindate"
              :maxdate="element.properties.maxdate"
            />
            <p class="error" v-if="errors.length > 0">
              {{ errors[0].replace("{field} ", "") }}
            </p>
          </template>
        </validation-provider>
      </div>
      <div
        v-else-if="element.type == 'form-textarea'"
        class="col-xs-12 bordered"
      >
        <validation-provider
          :rules="{
            required: element.properties.required,
            min: element.properties.minlength || 0,
            max: element.properties.maxlength || false,
          }"
        >
          <template slot-scope="{ errors }">
            <form-textarea
              :tabindex="index"
              v-bind="element.properties"
              @update="updateField($event, element.properties.label)"
              :value="formResponse[element.properties.label]"
              :name="element.properties.label"
              @keyup="onKeyUp($event, element.properties.label)"
              v-model="
                formResponse[`${element.properties.label}_${element.id}`]
              "
            />
            <p class="error" v-if="errors.length > 0">
              {{ errors[0].replace("{field} ", "") }}
            </p>
          </template>
        </validation-provider>
      </div>
      <div
        v-else-if="element.type == 'form-checkbox'"
        class="col-xs-12 bordered"
      >
        <validation-provider
          :rules="{ required: { allowFalse: !element.properties.required } }"
        >
          <template slot-scope="{ errors }">
            <form-checkbox
              v-bind="element.properties"
              @update="updateField($event, element.properties.label)"
              :value="formResponse[`${element.properties.label}_${element.id}`]"
              :data-vv-as="`${element.properties.label}_${element.id}`"
              :name="element.properties.label"
              @keyup="onKeyUp($event, element.properties.label)"
              v-model="
                formResponse[`${element.properties.label}_${element.id}`]
              "
            />
            <p class="error" v-if="errors.length > 0">
              {{ errors[0].replace("{field} ", "") }}
            </p>
          </template>
        </validation-provider>
      </div>
      <div v-else-if="element.type == 'form-radio'" class="col-xs-12 bordered">
        <validation-provider
          :rules="{ required: ( element.properties.required ? { allowFalse: element.properties.required } : element.properties.required ) }"
        >
          <template slot-scope="{ errors }">
            <form-radio
              :tabindex="index"
              v-bind="element.properties"
              :value="formResponse[element.properties.label]"
              :name="element.properties.label"
              v-model="
                formResponse[`${element.properties.label}_${element.id}`]
              "
            />
            <p class="error" v-if="errors.length > 0">
              {{ errors[0].replace("{field} ", "") }}
            </p>
          </template>
        </validation-provider>
      </div>
      <div
        v-else-if="['media-file-upload', 'media-file'].includes(element.type)"
        class="col-xs-12 bordered"
      >
        <media-file-upload
          v-bind="element.properties"
          v-model="formResponse[`${element.properties.title}_${element.id}`]"
        />
      </div>
    </section>
    <section class="row">
      <div class="col-xs-12">
        <base-button :primary="true" type="submit" v-if="!submitted">{{
          submitText
        }}</base-button>
      </div>
    </section>
  </validation-observer>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import FormBuilderColumn from "@/components/FormBuilderColumn.vue";
export default {
  name: "form-builder-form",
  components: {
    ValidationObserver,
    ValidationProvider,
    FormBuilderColumn,
  },
  props: {
    form: {
      type: [String, Object],
      default: null,
    },
    response: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: "form",
    },
  },
  data() {
    return {
      formResponse: {},
      submitted: false,
      test: "",
      elements: [],
      loading: true,
      submitText: "Submit Form",
    };
  },

  created() {
    if (!this.form) {
      return;
    }
    this.initForm();
  },
  watch: {
    form: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        this.initForm();
      },
    },
  },
  computed: {
    alreadyComplete() {
      return !this.form.multiple && this.form.user_has_submitted;
    },
    formSubmissions() {
      if (this.type == "survey") {
        return this.$store.state.Surveys.surveySubmissions;
      }
      return this.$store.state.Forms.formSubmissions;
    },
  },
  methods: {
    getLabel(element, id, dir) {
      const elem = JSON.parse(element);
      const title = elem.properties.label
        ? elem.properties.label
        : elem.properties.title;

      return `${title}_${id}${dir}`;
    },
    async initForm() {
      const newObject = {};
      Array.from(this.form.current_version.elements).forEach((elem) => {
        if (elem.properties) {
          newObject[elem.properties.label] = "";
        }
      });

      Object.assign({}, this.formResponse, newObject);

      //format the elements to be nice
      this.elements = await this.form.current_version.elements.map((elem) => {
        const toReturn = {
          type: elem.type,
          id: elem.id,
        };
        if (elem.content != "") {
          toReturn.content = elem.content;
        }
        if (elem.properties) {
          toReturn.properties = {};
          elem.properties.forEach((prop) => {
            if (
              ["required", "checked", "multiple", "other"].includes(prop.key)
            ) {
              toReturn.properties[prop.key] = prop.value === "1" ? true : false;
            } else {
              toReturn.properties[prop.key] = prop.value;
            }
          });
        }

        return toReturn;
      });

      if (this.type == "survey") {
        this.submitText = "Submit Survey";
      }

      this.loading = false;
    },
    async onSubmitForm() {
      //validate?

      const vm = this;

      const isValid = await this.$refs.observer.validate();

      if (!isValid) {
        vm.validationFailed = true;
        return;
      }

      // need to make sure that any empty ones have N/A

      this.elements.forEach((elem) => {
        const ignore = ["text-block", "base-heading"];
        if (elem.properties && !ignore.includes(elem.type)) {
          if (elem.type !== "base-row") {
            const label = elem.properties.label
              ? elem.properties.label
              : elem.properties.title;
            if (label && !this.formResponse[`${label}_${elem.id}`]) {
              this.formResponse[`${label}_${elem.id}`] = "N/A";
            }
          } else {
            const leftData = JSON.parse(elem.properties.leftColumn);
            if (!ignore.includes(leftData.type)) {
              const leftLabel = leftData.properties.label
                ? leftData.properties.label
                : leftData.properties.title;
              if (
                leftLabel &&
                !this.formResponse[`${leftLabel}_${elem.id}-1`]
              ) {
                this.formResponse[`${leftLabel}_${elem.id}-1`] = "N/A";
              }
            }
            const rightData = JSON.parse(elem.properties.rightColumn);
            if (!ignore.includes(leftData.type)) {
              const rightLabel = rightData.properties.label
                ? rightData.properties.label
                : rightData.properties.title;
              if (
                rightLabel &&
                !this.formResponse[`${rightLabel}_${elem.id}-2`]
              ) {
                this.formResponse[`${rightLabel}_${elem.id}-2`] = "N/A";
              }
            }
          }
        }
      });

      // need to make sure answers are in the correct order in case someone fills it out in a wierd order
      const finalResponse = {};
      const objKeys = Object.keys(this.formResponse).sort(
        (a, b) =>
          a.split("_")[1].replace("-", "") - b.split("_")[1].replace("-", "")
      );
      objKeys.forEach((key) => {
        finalResponse[key.split("-")[0]] = this.formResponse[key];
      });

      const response = Object.keys(finalResponse)
        .filter((key) => !key.includes("undefined")) // remove any undefined labels
        .reduce((obj, key) => {
          obj[key] = finalResponse[key];
          return obj;
        }, {}); // remove any unmatched answers

      const action =
        this.type == "survey"
          ? "Surveys/submitResponse"
          : "Forms/submitResponse";

      this.$store.dispatch(action, response).then((resp) => {
        vm.validationFailed = false;
        this.submitted = true;
      });
    },
    onKeyUp(ev, el) {
      this.formResponse[el] = ev;
    },
  },
};
</script>

<style lang="scss">
.mft-form-build {
  .row {
    padding: 1rem;
    .bordered {
      border-bottom: 1px solid $colour-nhs-med-grey;
      padding-bottom: 1rem;
    }
  }

  :focus {
    outline: none;
  }

  .error {
    color: red;
  }
}
</style>
